import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccounts } from '../contexts/AccountContext';
import Icon from './Icon';

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'داشبورد',
  '/stories': 'استوری‌ها',
  '/posts': 'پست‌ها',
  '/highlights': 'هایلایت‌ها',
  '/dm': 'دایرکت',
  '/inbox': 'صندوق ورودی',
  '/logs': 'گزارش رویدادها',
  '/analytics': 'تحلیل‌ها',
  '/settings': 'تنظیمات',
  '/connect': 'اتصال اینستاگرام',
  '/automations': 'اتومیشن‌ها',
};

interface TopbarProps {
  onMenuClick: () => void;
}

export default function Topbar({ onMenuClick }: TopbarProps) {
  const { pathname } = useLocation();
  const { user, signOut } = useAuth();
  const { accounts, selectedAccount, selectAccount, refreshAccounts } = useAccounts();
  const navigate = useNavigate();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);
  const title = PAGE_TITLES[pathname] ?? 'پنل اتوماسیون اینستاگرام';

  useEffect(() => {
    if (!switcherOpen) return;
    const onClick = (e: MouseEvent): void => {
      if (switcherRef.current !== null && !switcherRef.current.contains(e.target as Node)) {
        setSwitcherOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [switcherOpen]);

  async function handleLogout(): Promise<void> {
    await signOut();
    navigate('/login', { replace: true });
  }

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur lg:px-8">
      <button
        type="button"
        onClick={onMenuClick}
        className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
        aria-label="باز کردن منو"
      >
        <Icon name="menu" className="h-5 w-5" />
      </button>

      <h1 className="text-base font-bold text-slate-900 lg:text-lg">{title}</h1>

      <div className="ms-auto flex items-center gap-3">
        <div ref={switcherRef} className="relative">
          <button
            type="button"
            onClick={() => setSwitcherOpen((v) => !v)}
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
            aria-haspopup="listbox"
            aria-expanded={switcherOpen}
          >
            {selectedAccount !== null ? (
              <>
                {selectedAccount.profile_picture_url !== null ? (
                  <img
                    src={selectedAccount.profile_picture_url}
                    alt=""
                    className="h-5 w-5 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-100 text-[10px] font-bold text-brand-700">
                    {selectedAccount.username.slice(0, 1)}
                  </div>
                )}
                <span dir="ltr">@{selectedAccount.username}</span>
              </>
            ) : (
              <span className="text-slate-400">انتخاب حساب</span>
            )}
            <Icon name="chevron-down" className="h-3.5 w-3.5 text-slate-400" />
          </button>

          {switcherOpen && (
            <div
              className="absolute end-0 top-full z-30 mt-1 w-64 rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
              role="listbox"
            >
              <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  حساب‌های متصل
                </span>
                <button
                  type="button"
                  onClick={() => {
                    void refreshAccounts();
                    setSwitcherOpen(false);
                  }}
                  className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  aria-label="تازه‌سازی"
                >
                  <Icon name="refresh" className="h-3.5 w-3.5" />
                </button>
              </div>
              {accounts.length === 0 ? (
                <p className="px-3 py-3 text-xs text-slate-500">حسابی متصل نیست.</p>
              ) : (
                accounts.map((account) => (
                  <button
                    key={account.id}
                    type="button"
                    role="option"
                    aria-selected={account.id === selectedAccount?.id}
                    onClick={() => {
                      selectAccount(account.id);
                      setSwitcherOpen(false);
                    }}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-right text-xs transition-colors hover:bg-slate-50 ${
                      account.id === selectedAccount?.id
                        ? 'bg-brand-50 font-semibold text-brand-700'
                        : 'text-slate-700'
                    }`}
                  >
                    {account.profile_picture_url !== null ? (
                      <img
                        src={account.profile_picture_url}
                        alt=""
                        className="h-6 w-6 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-500">
                        {account.username.slice(0, 1)}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate" dir="ltr">
                        @{account.username}
                      </p>
                      <p className="truncate text-[10px] text-slate-400">{account.status}</p>
                    </div>
                    {account.id === selectedAccount?.id && (
                      <Icon name="check" className="h-3.5 w-3.5 shrink-0 text-brand-600" />
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {user !== null && (
          <span
            dir="ltr"
            className="hidden max-w-[180px] truncate text-xs text-slate-500 sm:inline"
          >
            {user.email}
          </span>
        )}
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-200 text-sm font-semibold text-slate-600">
          {user !== null ? user.email.slice(0, 1).toUpperCase() : 'ک'}
        </div>
        <button
          type="button"
          onClick={() => void handleLogout()}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
        >
          خروج
        </button>
      </div>
    </header>
  );
}
